/**
 * Action Synthesizer
 *
 * Converts an optimizer-authored per-step action plan into storable
 * `CachedAction[]` by routing through the SAME assembly functions the live
 * recording path uses (`assembleCachedActionsForStorage` +
 * `parameterizeActionsWithVariables`) — no parallel token logic.
 *
 * The plan carries the literal values observed in the trace; macro and
 * variable provenance from the run records lets the canonical assembly
 * reverse-map literals into `macroBindings` and `$var.X` bindings so the
 * playbook replays correctly on future runs with fresh values.
 *
 * @module optimizer/action-synthesizer
 */

import type { AssertionSpec, CachedAction, SelectorStrategy } from '@chatsdet/types';
import type { CachedActionRecord } from '../types.js';
import type { MacroResolutionLog } from '../macros/types.js';
import {
  assembleCachedActionsForStorage,
  parameterizeActionsWithVariables,
} from '../cached-action-assembly.js';

/**
 * One browser action on a node's most-direct path, as authored by the
 * optimizer agent. Values in `args` are the literals observed in the trace —
 * provenance mapping back to variables/macros is this module's job, not the
 * agent's.
 */
export interface PlannedBrowserAction {
  /** 1-based playbook step this action fulfills. */
  stepOrder: number;
  /** Tool name as the live agent would have called it (e.g. 'browser_click'). */
  toolName: string;
  /** Normalized action type (click | type | select_option | navigate | assert | …). */
  actionType: string;
  /** Literal tool args as observed in the trace (sanitized — no ephemeral refs). */
  args: Record<string, unknown>;
  /** Self-contained Playwright snippet for `run_code` actions. */
  code?: string;
  /** Offline-verified selector strategy for the target element. */
  selectors?: SelectorStrategy;
  /** Human-readable target description. */
  elementDescription?: string;
  /** Assertion spec for assertion steps. */
  assertion?: AssertionSpec;
}

/**
 * Action types whose replay resolves a target element and therefore requires
 * a verified selector. Everything else (navigate, wait_for, assert, capture,
 * evaluate, screenshot, resize, scroll, …) replays without one.
 */
const ELEMENT_TARGET_ACTION_TYPES = new Set(['click', 'type', 'select_option', 'hover']);

export interface SynthesizeActionsOptions {
  nodeId: string;
  planned: PlannedBrowserAction[];
  /** Macro provenance from the run records (expression → resolved value, keyed by input field). */
  macroResolutionLog?: MacroResolutionLog;
  /** Ad-hoc macro resolutions the agent rolled mid-run (resolved value → expression). */
  agentMacroResolutions?: Map<string, string>;
  /** Variable values in effect during the source run (for `$var.X` reverse-mapping). */
  variableValues?: Record<string, string>;
}

/**
 * Build storable `CachedAction[]` from the optimizer's plan via the canonical
 * assembly pipeline. Action indices are assigned by plan position.
 */
export function synthesizeCachedActions(opts: SynthesizeActionsOptions): CachedAction[] {
  const records: CachedActionRecord[] = opts.planned.map((p, i) => ({
    index: i,
    toolName: p.toolName,
    actionType: p.actionType,
    nodeId: opts.nodeId,
    selectors: p.selectors,
    args: p.args,
    code: p.code,
    elementDescription: p.elementDescription,
    assertion: p.assertion,
    // Only actions that resolve one top-level target require top-level
    // selectors. `fill_form` owns selectors per field; file uploads can use the
    // native chooser/fallback input; move uses coordinates. Selectorless
    // deterministic actions must replay structurally rather than becoming gaps.
    requiresAgent: ELEMENT_TARGET_ACTION_TYPES.has(p.actionType) && !p.selectors,
    selectorAuthor: p.selectors ? ('deterministic' as const) : undefined,
  }));

  const assembled = assembleCachedActionsForStorage({
    actions: records,
    macroResolutionLog: opts.macroResolutionLog,
    agentMacroResolutions: opts.agentMacroResolutions,
  });

  return opts.variableValues && Object.keys(opts.variableValues).length > 0
    ? parameterizeActionsWithVariables(assembled, opts.variableValues)
    : assembled;
}
